import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Form, Select, Checkbox, ConfigProvider } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownService,
  TenantPermissionService,
  TenantRoleService,
} from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import { filterByLabel } from "@/lib/filterByLabel";
import { checkFormChanges } from "@/lib/checkFormChanges";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import {
  AssignPermissionPayload,
  UpdatePermissionPayload,
} from "@/utils/types/tenantPermissions";

interface RolePermissionModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  initialData?: any;
  isEditMode: boolean;
}

const RolePermissionModal = ({
  open,
  onCancel,
  onSuccess,
  initialData,
  isEditMode,
}: RolePermissionModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { data: roleOptions } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  const { data: schemaOptions } = useQuery({
    queryKey: ["schema", "PTS"],
    queryFn: () =>
      DropdownService.getDropdownData("schema", { name__ilike: "%PTS%" }),
  });

  // Fetch permission data for edit mode if not provided
  const { data: permissionData, isLoading } = useQuery({
    queryKey: ["permission", initialData?.id],
    queryFn: () =>
      TenantPermissionService.getPermissionById(initialData?.id ?? ""),
    enabled: isEditMode && open && !!initialData?.id && !initialData?.role,
  });

  const permission = initialData ?? permissionData;

  // Set form values when permission data is available
  useEffect(() => {
    if (isEditMode && permission && open) {
      form.setFieldsValue({
        role: permission.role,
        schema_name: permission.schema_name,
        level: permission.level ?? 0,
        only_if_created: permission.only_if_created ?? false,
        is_active: permission.is_active ?? true,
        can_select: permission.can_select ?? false,
        can_read: permission.can_read ?? false,
        can_write: permission.can_write ?? false,
        can_create: permission.can_create ?? false,
        can_delete: permission.can_delete ?? false,
        can_print: permission.can_print ?? false,
        can_export: permission.can_export ?? false,
        can_import: permission.can_import ?? false,
        can_email: permission.can_email ?? false,
        can_report: permission.can_report ?? false,
        can_share: permission.can_share ?? false,
      });
    } else if (!isEditMode && open) {
      form.resetFields();
      form.setFieldsValue({
        is_active: true,
        level: 0,
      });
    }
  }, [permission, open, form, isEditMode]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      if (isEditMode) {
        const transformedData: UpdatePermissionPayload = {
          role: values.role,
          schema_name: values.schema_name,
          level: values.level ?? 0,
          only_if_created: values.only_if_created ?? false,
          is_active: values.is_active ?? true,
          can_select: values.can_select ?? false,
          can_read: values.can_read ?? false,
          can_write: values.can_write ?? false,
          can_create: values.can_create ?? false,
          can_delete: values.can_delete ?? false,
          can_print: values.can_print ?? false,
          can_export: values.can_export ?? false,
          can_import: values.can_import ?? false,
          can_email: values.can_email ?? false,
          can_report: values.can_report ?? false,
          can_share: values.can_share ?? false,
        };

        if (!checkFormChanges(transformedData, permission, notify)) {
          setIsSubmitting(false);
          return;
        }

        await TenantPermissionService.updatePermission(
          permission.id,
          transformedData
        );
        notify.success("Permission updated successfully");
      } else {
        const transformedData: AssignPermissionPayload = {
          role: values.role,
          schema_name: values.schema_name,
          level: values.level ?? 0,
          only_if_created: values.only_if_created ?? false,
          is_active: values.is_active ?? true,
          can_select: values.can_select ?? false,
          can_read: values.can_read ?? false,
          can_write: values.can_write ?? false,
          can_create: values.can_create ?? false,
          can_delete: values.can_delete ?? false,
          can_print: values.can_print ?? false,
          can_export: values.can_export ?? false,
          can_import: values.can_import ?? false,
          can_email: values.can_email ?? false,
          can_report: values.can_report ?? false,
          can_share: values.can_share ?? false,
        };

        await TenantRoleService.assignPermissionToRole(
          values.role,
          transformedData
        );
        notify.success("Permission created successfully");
      }

      onSuccess();
      form.resetFields();
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "create"} permission`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
  };

  if (isEditMode && isLoading) {
    return (
      <Dialog open={open} onOpenChange={handleCancel}>
        <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-4xl flex-col bg-white p-0 sm:w-full">
          <DialogHeader className="flex-shrink-0 px-6 pb-4 pt-6">
            <DialogTitle>Edit Permission</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-2xl flex-col bg-white p-0 sm:w-full">
        <DialogHeader className="flex-shrink-0 px-6 pb-4 pt-6">
          <DialogTitle>
            {isEditMode ? "Edit Permission" : "Create Permission"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the permission settings below"
              : "Configure permission settings for role and schema"}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={modalContentRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6"
        >
          <ConfigProvider
            getPopupContainer={() => {
              return modalContentRef.current ?? document.body;
            }}
          >
            <Form form={form} layout="vertical" className="space-y-4">
              <div className="grid gap-1 sm:grid-cols-2 sm:gap-4">
                <Form.Item
                  name="role"
                  label="Role"
                  rules={[{ required: true, message: "Please select a role" }]}
                >
                  <Select
                    disabled={isEditMode}
                    options={roleOptions}
                    placeholder="Select Role"
                    showSearch
                    filterOption={filterByLabel}
                  />
                </Form.Item>

                <Form.Item
                  name="schema_name"
                  label="Schema"
                  rules={[
                    { required: true, message: "Please select a schema" },
                  ]}
                >
                  <Select
                    disabled={isEditMode}
                    options={schemaOptions?.map((schema: any) => ({
                      value: schema.label,
                      label: schema.label,
                    }))}
                    placeholder="Select Schema"
                    showSearch
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* <Form.Item
                  name="level"
                  label="Level"
                  rules={[{ required: true, message: "Please enter level" }]}
                >
                  <InputNumber
                    min={0}
                    max={10}
                    placeholder="Enter level"
                    style={{ width: "100%" }}
                  />
                </Form.Item> */}

                {/* <Form.Item
                  label="Is Active"
                  name="is_active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item> */}
              </div>

              <Form.Item
                label="Only If Created"
                name="only_if_created"
                valuePropName="checked"
                className="mb-0"
              >
                <Checkbox />
              </Form.Item>

              <div className="space-y-4">
                <div>
                  <h4 className="mb-3 text-sm font-semibold">Permissions</h4>
                  <div className="flex flex-wrap gap-6">
                    <Form.Item
                      label="Read"
                      name="can_read"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Write"
                      name="can_write"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Create"
                      name="can_create"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Delete"
                      name="can_delete"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                  </div>
                </div>
                <div>
                  <h4 className="mb-3 text-sm font-semibold">
                    Additional Permissions
                  </h4>
                  <div className="flex flex-wrap gap-6">
                    <Form.Item
                      label="Select"
                      name="can_select"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Print"
                      name="can_print"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Export"
                      name="can_export"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Import"
                      name="can_import"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Email"
                      name="can_email"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Report"
                      name="can_report"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      label="Share"
                      name="can_share"
                      valuePropName="checked"
                      className="mb-0"
                    >
                      <Checkbox />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Form>
          </ConfigProvider>
        </div>

        <DialogFooter className="flex-shrink-0 flex-col gap-2 border-t px-6 pb-6 pt-4 sm:flex-row sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RolePermissionModal;
